import React from 'react';
import createAuthProvider from '../lib/AuthManager';
import createHttpClient from '../lib/HttpClient';
import { User, UserPerms } from '../models/organizationModels';
import { refreshToken } from '../services/organizationService';
import useSessionStorage from '../hooks/useSessionStorage';
import useLocalStorage from '../hooks/useLocalStorage';


/**
 * Token authetication managment
 */
export const [useAuth, authLogin, authLogout, getAuthData] = createAuthProvider(
    {
        storageKey: 'authLotiaApp',
    }
);

/**
 * App User managment
 */
let appUser: User | null = null;
export const setAppUser = (user: User | null) => {
    appUser = user;
}
export const getAppUser = () => {
    return appUser;
}

/*
 * Http Client
 */
export const [httpGet, httpPost, httpPut, httpDelete] = createHttpClient(
    {
        getAuthorizationAccessToken: () => {
            return getAuthData() ? "Bearer " + getAuthData().access : null;
        },
        onNotAuthorized: async (response: Response, resend: () => Promise<any>) => {
            const data = await response.json();
            if (response.status === 401 && data.errors != null && data.errors[0].token_type === "access") {
                const authData = await refreshToken();
                authLogin(authData);
                return resend();
            } else {
                authLogout();
                setAppUser(null);
                return Promise.reject(await response.json() || 'HTTP error');
            }
        }
    }
)

/**
 * App Data Context
 */
export interface AppData {
    showCopyright: boolean,
}

interface AppContextProps {
    data: AppData,
    setData: (value: AppData) => void,
    storeUserPerms: (perms: any) => void,
    userPerms: UserPerms | null,
    setMenuIndex: (index: number) => void,
    menuIndex: number | null,
    darkTheme: boolean,
    setDarkTheme: (value: boolean) => void,
    logout: () => void,
}

const defaultContext = {
    data: {
        showCopyright: true,
    },
}

export const AppContext = React.createContext<AppContextProps>(undefined!);

export function useAppContext(): AppContextProps {
    return React.useContext<AppContextProps>(AppContext);
}

const AppContextProvider = (props: { children: React.ReactNode; }) => {
    const [data, setData] = React.useState<AppData>(defaultContext.data);
    const [userPerms, setUserPerms] = useSessionStorage<UserPerms | null>('userPerms', null)
    const [menuIndex, setMenuIndex] = useSessionStorage<number>('menuIndex', 0)
    const [darkTheme, setDarkTheme] = useLocalStorage<boolean>('darkMode', false)

    const storeUserPerms = (perms: UserPerms) => {
        setUserPerms(perms);
    }
    
    const logout = (): void => {
        authLogout();
        setUserPerms(null);
    }

    return (
        <AppContext value={{ data, setData, storeUserPerms, userPerms, setMenuIndex, menuIndex, darkTheme, setDarkTheme, logout }}>
            {props.children}
        </AppContext>
    );
};

export default AppContextProvider;
